import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { DrawerService } from 'src/app/services/drawer.service';
import { NgForm, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { AccountService } from 'src/app/services/account.service';
import { Account, Credential, CredentialPage, CustomAccount } from 'src/app/types';
import {
  CreateCustomCredentialAccountRequest,
  UpdateCustomCredentialAccountRequest,
} from 'src/app/generated-api/models';
import { DRAWER_DATA } from 'src/app/shared/injector-tokens';
import { UiButtonDirective } from '../../../ui/button/button.directive';
import { DrawerFooterComponent } from '../../drawer-layout/drawer-footer.component';
import { DrawerLayoutComponent, DrawerLayoutState } from '../../drawer-layout/drawer-layout.component';
import { UiFieldComponent } from '../../../ui/field/field.component';
import { UiInputDirective } from '../../../ui/input/input.directive';
import { UiCurrencyInputComponent } from '../../../ui/currency-input/currency-input.component';
import { UiCheckboxComponent } from '../../../ui/checkbox/checkbox.component';

@Component({
  selector: 'custom-account-drawer-wrapper',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './custom-account-drawer-wrapper.component.html',
  imports: [
    DrawerLayoutComponent,
    DrawerFooterComponent,
    UiButtonDirective,
    ReactiveFormsModule,
    FormsModule,
    UiFieldComponent,
    UiInputDirective,
    UiCurrencyInputComponent,
    UiCheckboxComponent,
  ],
})
export class CustomAccountDrawerWrapperComponent {
  data = inject<{
    accountId?: string;
  }>(DRAWER_DATA);
  private drawer = inject(DrawerService);
  private accountService = inject(AccountService);

  readonly accountsQuery = injectQuery(() => this.accountService.accountPageQueryOptions());
  readonly addCustomAccountMutation = injectMutation(() => this.accountService.addCustomAccountMutationOptions());
  readonly editCustomAccountMutation = injectMutation(() => this.accountService.editCustomAccountMutationOptions());
  readonly deleteCustomAccountMutation = injectMutation(() => this.accountService.deleteCustomAccountMutationOptions());
  readonly customCredential = computed(() =>
    this.accountsQuery.data()?.data.find(credential => credential.type === 'CUSTOM'),
  );
  readonly ac = computed(() => {
    const accountPage = this.accountsQuery.data();
    if (!this.data.accountId) return null;
    const credential = this.credentialByAccountId(accountPage, this.data.accountId);

    return {
      account: credential?.accounts.find(account => account.id === this.data.accountId),
      credential,
    };
  });
  title = this.data.accountId ? 'Edit custom account' : 'Add custom account';
  action = this.data.accountId ? 'Save account' : 'Add custom account';
  // Both modes need the account page: saving posts to the custom credential, and editing
  // fills the form from the account. Show the form only once what it needs has loaded, so a
  // failed or incomplete load can't turn a submit into a silent no-op.
  readonly layoutState = computed<DrawerLayoutState>(() => {
    if (this.accountsQuery.isPending()) return 'loading';
    if (this.accountsQuery.isError() || !this.customCredential()) return 'error';
    if (this.data.accountId && !this.ac()?.account) return 'error';
    return 'ready';
  });
  readonly layoutErrorMessage = computed(() => {
    if (this.accountsQuery.isError()) return "We couldn't load your accounts. Check your connection and try again.";
    if (this.data.accountId && this.customCredential()) return "We couldn't find this account. It may have been removed.";
    return "Custom accounts aren't available right now. Try again later.";
  });
  private initializedCustomAccount = false;

  readonly customAccount = signal<
    Partial<CustomAccount> & { balance: { current: number }; includeInAvailableBalance: boolean }
  >({
    name: '',
    includeInAvailableBalance: true,
    balance: {
      current: 0,
    },
  });

  constructor() {
    effect(() => {
      if (!this.data.accountId || this.initializedCustomAccount) return;

      const account = this.ac()?.account;
      if (!account) return;

      this.customAccount.set({
        id: account.id,
        name: account.name,
        includeInAvailableBalance: account.includeInAvailableBalance,
        balance: { current: account.balance.current ?? 0 },
      });
      this.initializedCustomAccount = true;
    });
  }

  submitCustomAccount(form: NgForm) {
    if (!form.valid) return;

    const credential = this.customCredential();
    if (!credential) return;

    if (this.data.accountId) {
      this.editCustomAccountMutation.mutate(
        { data: this.buildUpdateCustomAccountRequest(), credentialId: credential.id },
        { onSuccess: () => this.drawer.closeDrawer() },
      );
      return;
    }

    this.addCustomAccountMutation.mutate(
      { data: this.buildCreateCustomAccountRequest(), credentialId: credential.id },
      { onSuccess: () => this.drawer.closeDrawer() },
    );
  }

  deleteCustomAccount() {
    const credential = this.ac()?.credential;
    if (!this.data.accountId || !credential) return;

    this.deleteCustomAccountMutation.mutate(
      { data: { id: this.data.accountId }, credentialId: credential.id },
      { onSuccess: () => this.drawer.closeDrawer() },
    );
  }

  private buildCreateCustomAccountRequest(): CreateCustomCredentialAccountRequest {
    const customAccount = this.customAccount();
    return {
      name: customAccount.name ?? '',
      balance: {
        current: Number(customAccount.balance.current ?? 0),
      },
      includeInAvailableBalance: customAccount.includeInAvailableBalance,
    };
  }

  private credentialByAccountId(accountPage: CredentialPage | undefined, accountId: string): Credential | undefined {
    return accountPage?.data.find(credential =>
      credential.accounts.some((account: Account) => account.id === accountId),
    );
  }

  private buildUpdateCustomAccountRequest(): UpdateCustomCredentialAccountRequest & { id: string } {
    const accountId = this.data.accountId;
    if (!accountId) throw new Error('An account ID is required to update a custom account.');

    return {
      id: accountId,
      ...this.buildCreateCustomAccountRequest(),
    };
  }
}
